import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  LabelDisplayedRowsArgs,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField
} from '@mui/material';
import {
  AddCircle,
  AssignmentReturned,
  Close,
  CloudDownload,
  GetApp,
  Search
} from '@mui/icons-material';
import { Theme } from '@mui/system';
import { buttons, Colors, shapes } from '../../../utils/theme';
import { displayText } from '../../../utils/ui';
import Loader from '../Loader';
import details from '../../../assets/images/actions/details.svg';
import remove from '../../../assets/images/actions/trash.svg';
import remove_gray from '../../../assets/images/actions/trash_gray.svg';
import { Roles } from '../../../enums/RoleEnums';
import { Types } from '../../../enums/TypeEnums';
import { canUserDelete } from '../../../utils/rightUtils';
import { AppContext } from '../../../AppContext';
import { INVOICE_STATUS_CONFIRMED } from '../../../enums/AppConsts';

type Order = 'asc' | 'desc';

type Data = any;

interface IProps {
  columns: IHeadCell[];
  data: Data[];
  isLayoutFixed?: boolean;
  hidePagination?: boolean;
  rowsPerPageOptions?: number[];
  pageSize?: number;
  createButtonProps?: CreateButtonProps;
  exportButtonProps?: IActionButtonProps;
  importButtonProps?: IActionButtonProps;
  downloadButtonProps?: IActionButtonProps;
  hideSearch?: boolean;
  handleDetails?: (rowData: Data) => void;
  handleRemove?: (rowData: Data) => void;
  additionalFilters?: JSX.Element;
  loading?: boolean;
  crudType?: string;
  // Server-side pagination support
  page?: number;
  onPageChange?: (event: unknown, newPage: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  totalCount?: number; // Total count for server-side pagination
  // Server-side sorting support
  orderBy?: string | null;
  orderDirection?: 'asc' | 'desc';
  onSortChange?: (orderBy: string | null, orderDirection: 'asc' | 'desc') => void;
}

interface IActionButtonProps {
  id: string;
  onClick: () => void;
}

interface ITableHeadersProps {
  columns: IHeadCell[];
  order: Order;
  orderBy: IHeadCell | null;
  onRequestSort: (property: IHeadCell) => void;
}

export interface IHeadCell {
  title: string;
  field?: string;
  type?: 'string' | 'numeric' | 'boolean' | 'date';
  render?: (data: Data) => string | JSX.Element;
  sorting?: boolean;
  customSort?: (itemA: Data, itemB: Data) => number;
  searchable?: boolean;
  width?: number;
  customFilterAndSearch?: (filter: string, rowData: Data, columnDef: IHeadCell) => boolean;
}

export interface CreateButtonProps {
  id: string;
  onClick: () => void;
  text: string;
  disabled?: boolean;
}

const boxShadowStyle: SxProps = {
  boxShadow: '0px 10px 20px #69AAF366'
};

const getTableRowCellStyle = (index: number, colLength: number, width: number | undefined) =>
  ({
    border: 'none',
    left: 0,
    textAlign: 'center',
    color: 'primary.main',
    padding: 0,
    width: width ? `${width}px` : 'auto',
    zIndex: index === 0 ? 2 : 1,
    borderTopLeftRadius: index === 0 ? '2px' : 0,
    borderBottomLeftRadius: index === 0 ? '2px' : 0,
    borderBottomRightRadius: index + 1 === colLength ? '2px' : 0,
    borderTopRightRadius: index + 1 === colLength ? '2px' : 0,
    borderRight: index + 1 === colLength ? 'none' : `0.5px solid ${Colors.third}`
  } as SxProps);

const tableRowStyle: SxProps = {
  background: '#FFFFFF 0% 0% no-repeat padding-box',
  height: '60px',
  color: Colors.main,
  textAlign: 'center',
  opacity: 1,
  ...boxShadowStyle,
  border: 'none',
  '&.MuiTableRow-hover': {
    '&:hover': {
      backgroundColor: '#E8F3FF',
      '& td': {
        backgroundColor: '#E8F3FF'
      }
    }
  }
};

const tableHeaderCellFontStyle: SxProps = {
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '15px',
  zIndex: 1,
  textAlign: 'center',
  color: Colors.main
};

const tableHeaderCellStyle: SxProps<Theme> = {
  borderRight: '1px solid #FFFFFF',
  ...tableHeaderCellFontStyle
};

const actionButtonIconStyle: SxProps = { marginRight: '7px' };

const TableHeaders = ({ columns, order, orderBy, onRequestSort }: ITableHeadersProps) => (
  <TableHead>
    <TableRow
      sx={{
        background: '#69AAF399 0% 0% no-repeat padding-box',
        height: '42px',
        ...boxShadowStyle
      }}
    >
      {columns.map((col, index) => (
        <TableCell
          key={index}
          align={col.type === 'numeric' ? 'right' : 'left'}
          padding="none"
          sortDirection={orderBy === col ? order : false}
          sx={{
            ...tableHeaderCellStyle,
            padding: col.sorting !== false ? 0 : '0 13px',
            width: col.width ? `${col.width}px` : 'auto'
          }}
        >
          {col.sorting !== false ? (
            <TableSortLabel
              active={orderBy === col}
              direction={orderBy === col ? order : 'asc'}
              onClick={() => onRequestSort(col)}
              sx={{
                marginLeft: col.type === 'numeric' ? 0 : '26px',
                marginRight: col.type !== 'numeric' ? 0 : '26px'
              }}
            >
              <Box sx={tableHeaderCellFontStyle}>{col.title}</Box>
            </TableSortLabel>
          ) : (
            col.title
          )}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

function descendingComparator<T>(a: T, b: T, orderBy: IHeadCell): number {
  if (orderBy.customSort) {
    return orderBy.customSort(a, b);
  } else {
    const item1 = getCellValue(a, orderBy.field ?? '');
    const item2 = getCellValue(b, orderBy.field ?? '');
    if (orderBy.type === 'numeric' || orderBy.type === 'date') {
      return Number(item1) - Number(item2);
    } else if (orderBy.type === 'boolean') {
      return (item1.toLowerCase() === 'true' ? 1 : 0) - (item2.toLowerCase() === 'true' ? 1 : 0);
    } else {
      return item1.localeCompare(item2);
    }
  }
}

export function getComparator<Key extends keyof Data>(
  order: Order,
  orderBy: IHeadCell
): (a: { [key in Key]: Data }, b: { [key in Key]: Data }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export const getCellValue = (rowData: Data, path: string): string => {
  const parts = path.split('.');
  if (parts.length == 1) {
    return rowData[parts[0]] !== undefined && rowData[parts[0]] !== null
      ? rowData[parts[0]].toString()
      : '';
  } else {
    if (rowData[parts[0]]) {
      return getCellValue(rowData[parts[0]], parts.slice(1).join('.'));
    } else {
      return '';
    }
  }
};

const OverviewTable: React.FunctionComponent<IProps> = ({
  columns,
  data,
  isLayoutFixed,
  hidePagination,
  rowsPerPageOptions,
  pageSize,
  createButtonProps,
  exportButtonProps,
  importButtonProps,
  downloadButtonProps,
  hideSearch,
  handleDetails,
  handleRemove,
  loading = false,
  crudType,
  additionalFilters,
  page: controlledPage,
  onPageChange: controlledOnPageChange,
  onRowsPerPageChange: controlledOnRowsPerPageChange,
  totalCount,
  orderBy: controlledOrderBy,
  orderDirection: controlledOrderDirection,
  onSortChange: controlledOnSortChange
}: IProps) => {
  const { state } = useContext(AppContext);
  const deletableItems = !data || data.length === 0 ? false : canUserDelete(state.user, crudType);
  const itemsPerPageOptions = rowsPerPageOptions ?? [10, 15, 25, 50];

  // Use controlled or uncontrolled sorting
  const isControlledSorting =
    controlledOrderBy !== undefined && controlledOnSortChange !== undefined;
  const [internalOrder, setInternalOrder] = useState<Order>('asc');
  const [internalOrderBy, setInternalOrderBy] = useState<IHeadCell | null>(null);

  const order = isControlledSorting ? controlledOrderDirection || 'asc' : internalOrder;
  const orderBy = isControlledSorting
    ? controlledOrderBy
      ? columns.find((col) => col.field === controlledOrderBy) || null
      : null
    : internalOrderBy;
  const [internalPage, setInternalPage] = useState(0);
  const [internalItemsPerPage, setInternalItemsPerPage] = useState(
    pageSize ?? itemsPerPageOptions.length > 1 ? itemsPerPageOptions[1] : itemsPerPageOptions[0]
  );
  const [searchValue, setSearchValue] = useState('');
  const [filteredData, setFilteredData] = useState(data);

  // Use controlled or uncontrolled pagination
  const isControlledPagination = controlledPage !== undefined;
  const page = isControlledPagination ? controlledPage : internalPage;
  const itemsPerPage = isControlledPagination
    ? pageSize ?? internalItemsPerPage
    : internalItemsPerPage;

  useEffect(() => {
    setFilteredData(data);
    if (!hideSearch) {
      filterData(searchValue);
    }
    // Only reset page if pagination is not controlled
    if (!isControlledPagination) {
      setInternalPage(0);
    }
  }, [data, isControlledPagination]);

  const handleRequestSort = (property: IHeadCell) => {
    if (isControlledSorting && controlledOnSortChange) {
      // Controlled sorting: notify parent
      // 3-state cycle: no sort -> asc -> desc -> no sort
      const isCurrentColumn = orderBy === property;
      const isAsc = isCurrentColumn && order === 'asc';
      const isDesc = isCurrentColumn && order === 'desc';

      let newOrderBy: IHeadCell | null;
      let newOrder: 'asc' | 'desc';

      if (!isCurrentColumn) {
        // Different column: start with ascending
        newOrderBy = property;
        newOrder = 'asc';
      } else if (isAsc) {
        // Same column, currently asc: switch to desc
        newOrderBy = property;
        newOrder = 'desc';
      } else if (isDesc) {
        // Same column, currently desc: clear sorting
        newOrderBy = null;
        newOrder = 'asc';
      } else {
        // Fallback case (should not happen in normal flow): default to ascending
        newOrderBy = property;
        newOrder = 'asc';
      }

      controlledOnSortChange(newOrderBy?.field || null, newOrder);
    } else {
      // Uncontrolled sorting: manage state internally
      const isAsc = orderBy === property && order === 'asc';
      setInternalOrder(isAsc ? 'desc' : 'asc');
      const newOrderBy = isAsc ? property : orderBy !== property ? property : null;
      setInternalOrderBy(newOrderBy);
    }
  };

  const getReadyToUseData = () => {
    let rows = filteredData.slice();
    // Only sort on client side if not using controlled sorting (server-side sorting)
    if (!isControlledSorting && orderBy !== null) {
      rows = rows.sort(getComparator(order, orderBy));
    }
    // Don't slice data if using server-side pagination (hidePagination or controlled)
    if (hidePagination !== true && !isControlledPagination) {
      rows = rows.slice(page * itemsPerPage, page * itemsPerPage + itemsPerPage);
    }
    return rows;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    if (isControlledPagination && controlledOnPageChange) {
      controlledOnPageChange(event, newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    if (isControlledPagination && controlledOnRowsPerPageChange) {
      controlledOnRowsPerPageChange(event);
    } else {
      setInternalItemsPerPage(newItemsPerPage);
      setInternalPage(0);
    }
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearchValue(value);
    filterData(value);
    // Reset page when search changes
    if (isControlledPagination && controlledOnPageChange) {
      controlledOnPageChange(event, 0);
    } else {
      setInternalPage(0);
    }
  };

  const filterData = (filter: string) => {
    const trimmedSearchText = filter.trim();
    setFilteredData(
      data.filter((row) => {
        return columns
          .filter((columnDef) => {
            return columnDef.searchable === undefined ? true : columnDef.searchable;
          })
          .some((columnDef) => {
            if (columnDef.customFilterAndSearch) {
              return !!columnDef.customFilterAndSearch(trimmedSearchText, row, columnDef);
            } else if (columnDef.field) {
              const value = getCellValue(row, columnDef.field);
              return value?.toString().toUpperCase().includes(trimmedSearchText.toUpperCase());
            }
          });
      })
    );
  };

  const generateToolbar = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'end',
          marginBottom: '14px',
          padding: '0 24px',
          gap: '20px'
        }}
      >
        <Box sx={{ flexGrow: 0 }}>
          {hideSearch !== true && (
            <TextField
              id="table-search-field"
              value={searchValue}
              onChange={handleValueChange}
              sx={{
                color: Colors.main,
                backgroundColor: Colors.input_field_background,
                maxWidth: '365px',
                height: '36px',
                borderRadius: '9px',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  }
                }
              }}
              placeholder={'Search...'}
              InputProps={{
                startAdornment: (
                  <Search
                    sx={{
                      color: Colors.main,
                      width: '20px',
                      height: '20px',
                      marginRight: '10px',
                      input: { '&::placeholder': { color: Colors.main } }
                    }}
                  />
                ),
                endAdornment: (
                  <IconButton
                    disabled={!Boolean(searchValue)}
                    onClick={() => {
                      setSearchValue('');
                      filterData('');
                      // Reset page when clearing search
                      if (isControlledPagination && controlledOnPageChange) {
                        controlledOnPageChange(null, 0);
                      } else {
                        setInternalPage(0);
                      }
                    }}
                  >
                    <Close
                      sx={{
                        color: searchValue ? Colors.main : '#9CA3AE',
                        width: '20px',
                        height: '20px'
                      }}
                    />
                  </IconButton>
                ),
                style: {
                  height: '36px',
                  color: Colors.main,
                  paddingRight: '4px'
                }
              }}
              className={'custom-search'}
            />
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }}>{additionalFilters}</Box>
        <Box sx={{ flexGrow: 0, display: 'flex', gap: '10px' }}>
          {exportButtonProps && (
            <Button
              id={exportButtonProps.id}
              data-testid={exportButtonProps.id}
              onClick={() => exportButtonProps.onClick()}
              sx={buttons.cancel}
            >
              <AssignmentReturned sx={actionButtonIconStyle} />
              Export
            </Button>
          )}
          {importButtonProps && (
            <Button
              id={importButtonProps.id}
              data-testid={importButtonProps.id}
              onClick={() => importButtonProps.onClick()}
              sx={buttons.cancel}
            >
              <GetApp sx={actionButtonIconStyle} /> Import
            </Button>
          )}
          {downloadButtonProps && (
            <Button
              id={downloadButtonProps.id}
              data-testid={downloadButtonProps.id}
              onClick={() => downloadButtonProps.onClick()}
              sx={buttons.cancel}
            >
              <CloudDownload sx={actionButtonIconStyle} /> Download
            </Button>
          )}
          {createButtonProps && (
            <Button
              id={createButtonProps.id}
              onClick={() => createButtonProps.onClick()}
              disabled={createButtonProps.disabled || false}
              variant="contained"
              color="primary"
              sx={buttons.overviewCreate}
            >
              <AddCircle sx={actionButtonIconStyle} />
              {createButtonProps.text}
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  const shouldShowToolbar =
    hideSearch !== true ||
    createButtonProps ||
    exportButtonProps ||
    importButtonProps ||
    downloadButtonProps ||
    additionalFilters;

  const detailsButton = (rowData: Data) => (
    <Button
      id={
        rowData.name
          ? rowData.name
          : rowData.number
          ? rowData.number
          : `${rowData.email}-${rowData.company?.name}`
      }
      data-testid={rowData.id}
      sx={{ width: 'auto' }}
      onClick={() =>
        handleDetails
          ? handleDetails(rowData)
          : console.log('Missing handle details function', rowData)
      }
    >
      <img src={details} alt="detailsButton" />
    </Button>
  );

  const removeButton = (rowData: Data) => {
    const disableDelete = shouldDisableDelete(rowData);
    return (
      <Button
        id={rowData.name ? `${rowData.name}-delete` : `${rowData.email}-delete`}
        data-testid={`${rowData.id}-delete`}
        sx={{ width: 'auto' }}
        disabled={disableDelete}
        onClick={() =>
          handleRemove
            ? handleRemove(rowData)
            : console.log('Missing handle remove function', rowData)
        }
      >
        {disableDelete ? (
          <img src={remove_gray} alt="removeButtonGray" />
        ) : (
          <img src={remove} alt="removeButton" />
        )}
      </Button>
    );
  };

  const shouldDisableDelete = (rowData: Data) => {
    if (rowData.__typename === Types.Invoice && rowData.status.name === INVOICE_STATUS_CONFIRMED) {
      return true;
    } else if (rowData.__typename === Types.User) {
      const restrictedRoles = [Roles.Admin, Roles.InvoiceEditor];
      return restrictedRoles.some((r) => rowData.roles.includes(r));
    } else if (
      rowData.__typename === Types.Index ||
      rowData.__typename === Types.IndexSeries ||
      rowData.__typename === Types.ETF ||
      rowData.__typename === Types.Stock
    ) {
      const restrictedRoles = [Roles.Admin, Roles.IndexOperator];
      return !restrictedRoles.some((r) => state.user.data?.roles.includes(r));
    } else {
      return !deletableItems;
    }
  };

  const styledColumns = columns.map((column) => {
    let res = {
      ...column
    };
    if (column.title === 'Details') {
      res = {
        ...res,
        render: detailsButton
      };
    } else if (column.title === 'Remove') {
      res = {
        ...res,
        render: removeButton
      };
    }
    return res;
  });

  return (
    <Paper
      sx={{
        width: '100%',
        padding: `${shouldShowToolbar ? '32px' : '14px'} 0`,
        boxShadow: '0px 10px 15px rgba(8, 79, 180, 0.05)',
        borderRadius: '8px'
      }}
    >
      {shouldShowToolbar && generateToolbar()}
      <TableContainer>
        <Table
          aria-labelledby="tableTitle"
          sx={{
            tableLayout: isLayoutFixed === true ? 'fixed' : 'default',
            borderCollapse: 'separate',
            borderSpacing: '0 18px',
            padding: '0 10px'
          }}
        >
          <TableHeaders
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            columns={columns}
          />
          <TableBody>
            {getReadyToUseData().map((rowData, index) => (
              <TableRow
                key={index}
                hover
                // onClick={handleDetails ? () => handleDetails(rowData) : () => true}
                sx={{
                  ...tableRowStyle,
                  cursor: handleDetails ? 'pointer' : 'default'
                }}
              >
                {styledColumns.map((col, index2) => (
                  <TableCell
                    key={index2}
                    sx={getTableRowCellStyle(index2, columns.length, col.width)}
                  >
                    {col.render
                      ? col.render(rowData)
                      : displayText(getCellValue(rowData, col.field ?? ''), col.type)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {hidePagination !== true && (
        <TablePagination
          rowsPerPageOptions={itemsPerPageOptions}
          showFirstButton={true}
          showLastButton={true}
          component="div"
          count={totalCount !== undefined ? totalCount : filteredData.length}
          rowsPerPage={itemsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          backIconButtonProps={{
            contextMenu: 'back',
            color: 'info',
            title: 'Previous'
          }}
          labelDisplayedRows={({ from, to, count }: LabelDisplayedRowsArgs) => {
            if (isControlledPagination && totalCount !== undefined) {
              // Server-side pagination: show actual range
              const actualFrom = count > 0 ? from : 0;
              const actualTo = Math.min(to, count);
              return `${actualFrom}-${actualTo} of ${count}`;
            }
            // Client-side pagination: show page info
            const pageCount = count > itemsPerPage ? Math.floor(count / itemsPerPage + 1) : 1;
            return `${page + 1} of ${pageCount}`;
          }}
          nextIconButtonProps={{
            color: 'info',
            title: 'Next'
          }}
          labelRowsPerPage={
            <span style={{ fontSize: '14px', lineHeight: '17px', color: Colors.gray }}>
              Total:{' '}
              <span style={{ color: Colors.skyBlue, fontSize: '12px', fontWeight: 700 }}>
                {totalCount !== undefined ? totalCount : data.length}
              </span>
            </span>
          }
          SelectProps={{
            id: 'table-pagination-select',
            renderValue: (props) => `${props} Rows/Page`,
            sx: {
              color: Colors.main,
              border: `1px solid ${Colors.main}`,
              borderRadius: `${shapes.borderRadius}px`,
              backgroundColor: Colors.white,
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: 700,
              marginRight: '20px',
              '&.Mui-focused div': {
                borderRadius: `${shapes.borderRadius}px`,
                backgroundColor: Colors.white
              }
            }
          }}
        />
      )}
      <Loader loading={loading} />
    </Paper>
  );
};

export default OverviewTable;
